import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { useEffect, useState, useContext } from 'react'
import { FooterDCP } from '../components/FooterDCP'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { SearchField } from '../components/SearchField'
import { useC216SubtenantDeviceGet } from '@/hooks/useC216SubtenantDeviceGet'
import { loadStatus } from '../constants/loadStatus'
import { useAuth } from '@/hooks/useAuth'
import { errorMsg } from '@/constants/messages'
import { LanguageContext } from '@/util/LanguageContext'
import { SubtenantDevice } from '@/interfaces/DeviceIf'

interface CustomerInfo {
  tenant: string
  serial_number: string
  cotopat_id: string
  android_id: string
  plan: string
  ratePlanName: string
  device_type: string
  start_date: string
  end_date: string
  registration_date: string
  app_version: string
  is_unassigned?: boolean
}

const formatYmdByLocale = (
  unixSeconds: number | null | undefined,
  locale: string,
  fallback: string,
): string => {
  if (unixSeconds == null) return fallback
  const d = new Date(unixSeconds * 1000)
  if (Number.isNaN(d.getTime())) return fallback
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

// 顧客情報管理画面
export const CustomerInfoManagement = () => {
  const auth = useAuth()
  const [allCustomers, setAllCustomers] = useState<CustomerInfo[]>([])
  const [displayCustomers, setDisplayCustomers] = useState<CustomerInfo[]>([])
  const [sortField, setSortField] = useState<keyof CustomerInfo | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const c216 = useC216SubtenantDeviceGet()
  const [openErrorDialog, setOpenErrorDialog] = useState(false)
  const [errorDialogText, setErrorDialogText] = useState('')
  const translation = useContext(LanguageContext)
  const loading = c216.loading === loadStatus.LOADING

  const getSubtenantDevice = async () => {
    const result = await c216.execSubtenantDeviceGet(auth.tenant)
    if (!result.success) {
      if (result.message === 'error_legal_not_accepted') {
        redirectToProviderPortal()
        return
      }
      setErrorDialogText(result.message ?? errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }

    const customers: CustomerInfo[] = (result.info ?? []).flatMap(customer => {
      //ライセンスが存在しないテナントは処理をスキップ
      if (!customer.licenses || (Array.isArray(customer.licenses) && customer.licenses.length === 0)) {
        return []
      }

      const licenseRows: CustomerInfo[] = []
      const licenseList = Array.isArray(customer.licenses) ? customer.licenses : [customer.licenses]
      const deviceList = Array.isArray(customer.devices) ? customer.devices : [customer.devices]
      licenseList.forEach(license => {
        const count = typeof license.device_limit === 'number'
          ? license.device_limit
          : (license.device_limit.mobile + license.device_limit.screen)

        //license.device_limitの数だけループ
        for (let i = 0; i < count; i++) {
          let device: SubtenantDevice | undefined = undefined

          if(license.license_type === 'term'){
            const device_id = license.device_id_list?.[i]
            device = deviceList.find(d => d.device_id === device_id)
            const addedDate = license.device_info?.[device_id]?.added_date ?? null
            if (device) device['registration_date'] = addedDate
          }else if(license.license_type === 'subscription'){
            const isScreen = i < license.device_limit.screen
            const device_id = isScreen
              ? license.device_id_list_screen?.[i]
              : license.device_id_list_mobile?.[i - license.device_limit.screen]
            const deviceInfo = isScreen ? license.device_info_screen : license.device_info_mobile
            device = deviceList.find(d => d.device_id === device_id)
            const addedDate = deviceInfo?.[device_id]?.added_date ?? null
            if (device) device['registration_date'] = addedDate
          }

          const userLanguage = navigator.language || navigator.languages?.[0] || 'en-US'
          licenseRows.push({
            tenant: license.tenant,
            serial_number: license.license_id,
            cotopat_id: device?.cotopat_id ?? '',
            android_id: device?.device_id ?? '',
            plan: license.license_type,
            ratePlanName: license.license_type === 'subscription' ? license.ratePlanName : '',
            device_type: license.license_type === 'term' ? license.device_type : '',
            start_date: formatYmdByLocale(license.start_date, userLanguage, ''),
            end_date: formatYmdByLocale(license.expiration_date, userLanguage, ''),
            registration_date: formatYmdByLocale(device?.registration_date, userLanguage, ''),
            app_version: device?.application_version ?? '',
          })
        }

      })

      // 未割当デバイスを収集して赤文字行として追加
      const assignedDeviceIds = new Set<string>()
      licenseList.forEach(license => {
        if (license.license_type === 'term') {
          license.device_id_list.forEach((id: string) => id && assignedDeviceIds.add(id))
        } else {
          license.device_id_list_screen.forEach((id: string) => id && assignedDeviceIds.add(id))
          license.device_id_list_mobile.forEach((id: string) => id && assignedDeviceIds.add(id))
        }
      })
      const tenantName = Array.isArray(customer.licenses)
        ? (customer.licenses[0]?.tenant ?? '')
        : (customer.licenses?.tenant ?? '')
      deviceList
        .filter(d => d?.device_id && !assignedDeviceIds.has(d.device_id))
        .forEach(device => {
          licenseRows.push({
            tenant: tenantName,
            serial_number: '',
            cotopat_id: device.cotopat_id ?? '',
            android_id: device.device_id ?? '',
            plan: '',
            ratePlanName: '',
            device_type: '',
            start_date: '',
            end_date: '',
            registration_date: '',
            app_version: device.application_version ?? '',
            is_unassigned: true,
          })
        })

      return licenseRows
    })

    setAllCustomers(customers)
    setDisplayCustomers(customers)
  }

  const redirectToProviderPortal = () => {
    const redirectUrl = new URL(window.location.href)
    redirectUrl.pathname = redirectUrl.pathname.replace(/\/customerinfomanagement\/?$/, '/')

    const lastCotopatIndex = redirectUrl.hostname.lastIndexOf('cotopat')
    if (lastCotopatIndex !== -1) {
      redirectUrl.hostname = `${redirectUrl.hostname.slice(0, lastCotopatIndex)}provider-portal${redirectUrl.hostname.slice(lastCotopatIndex + 'cotopat'.length)}`
    }

    window.location.replace(redirectUrl.toString())
  }

  useEffect(() => {
    auth.setIsDisable(loading)
  }, [auth, loading])

  useEffect(() => {
    getSubtenantDevice()
  }, [])

  // 検索ハンドラ（固定データをフロントエンドでフィルタ）
  const searchHandler = (keyword: string) => {
    if (!keyword.trim()) {
      setDisplayCustomers(allCustomers)
      setPage(0)
      return
    }
    const filtered = allCustomers.filter(
      c =>
        c.tenant.toLowerCase().includes(keyword.toLowerCase()) ||
        c.cotopat_id.toLowerCase().includes(keyword.toLowerCase()) ||
        c.serial_number.toLowerCase().includes(keyword.toLowerCase()) ||
        c.android_id.toLowerCase().includes(keyword.toLowerCase()),
    )
    setDisplayCustomers(filtered)
    setPage(0)
  }

  const handleSort = (field: keyof CustomerInfo) => {
    const isAsc = sortField === field && sortDir === 'asc'
    setSortDir(isAsc ? 'desc' : 'asc')
    setSortField(field)
    setPage(0)
  }

  const sortedRows = [...displayCustomers].sort((a, b) => {
    if (!sortField) return 0
    const aVal = a[sortField]
    const bVal = b[sortField]
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const formatPlanLabel = (plan: string | null | undefined, ratePlanName: string | null | undefined, device_type: string) => {
    if (!plan) return ''
    const p = plan.toLowerCase()
    if (p === 'subscription') return ratePlanName
    if (p === 'term') return device_type + ' license'
    return plan
  }

  const formatDeviceType = (type: string | null | undefined) => {
    if (!type) return ''
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
  }

  const columns: { field: keyof CustomerInfo; label: string }[] = [
    { field: 'tenant', label: translation['label_customer_info_orgname'] },
    { field: 'serial_number', label: translation['label_customer_info_serial_number'] },
    { field: 'cotopat_id', label: translation['label_customer_info_cotopat_id'] },
    { field: 'android_id', label: translation['label_customer_info_android_id'] },
    { field: 'plan', label: translation['label_customer_info_plan'] },
    { field: 'start_date', label: translation['label_customer_info_start_date'] },
    { field: 'end_date', label: translation['label_customer_info_end_date'] },
    { field: 'registration_date', label: translation['label_customer_info_registration_date'] },
    { field: 'app_version', label: translation['label_app_version'] },
  ]

  const headerDividerSx = {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '25%',
      height: '50%',
      borderRight: '1px solid #ccc',
    },
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ececec',
      }}
    >
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2 }}>
        <Typography sx={{ font: 'normal normal bold 34px/40px Noto Sans JP', color: '#202020' }}>
          Cotopat
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 3, md: 6 },
          pt: 2,
          maxWidth: 1160,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 110px)',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography sx={{ font: 'normal normal bold 28px/36px Noto Sans JP', color: '#202020', whiteSpace: 'pre-wrap' }}>
            {translation['label_device_info_management']}
          </Typography>
          <SearchField onKeyDown={searchHandler} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            backgroundColor: '#f7f7f7',
          }}
        >
          <TableContainer>
            <Table sx={{ '& .MuiTableCell-root': { px: 1.5 } }}>
              <TableHead>
                <TableRow>
                  {columns.map((col, i) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        fontWeight: 700,
                        borderBottom: '2px solid #3e3e3e',
                        ...(i < columns.length - 1 ? headerDividerSx : {}),
                      }}
                    >
                      <TableSortLabel
                        active={sortField === col.field}
                        direction={sortField === col.field ? sortDir : 'asc'}
                        onClick={() => handleSort(col.field)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row, i) => (
                  <TableRow key={`${row.tenant}-${row.android_id}-${i}`}>
                    {columns.map(col => (
                      <TableCell key={col.field} sx={row.is_unassigned ? { color: '#d32f2f' } : undefined}>
                        {col.field === 'plan' ? (
                          <>
                            {formatPlanLabel(row.plan , row.ratePlanName , row.device_type)}
                          </>
                        ) : row[col.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedRows.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage={translation['label_rows_per_page']}
            labelDisplayedRows={({ from, to, count }) =>
              (translation['label_display_separator'] ?? '')
                .replace('{{item}}', `${from}\u2013${to}`)
                .replace('{{item2}}', String(count))
            }
          />
        </Paper>
      </Box>

      <FooterDCP />

      {openErrorDialog && (
        <ModalConfirmDialog
          open={openErrorDialog}
          text={errorDialogText}
          onClick={() => setOpenErrorDialog(false)}
        />
      )}

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
            pointerEvents: 'auto',
            cursor: 'wait',
            zIndex: 1500,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  )
}
